import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {TITLE_SUFFIX} from '../../config/constants';
import {GSSKT_HOST} from '../../config/constants/hosts';
import {COMPANIES} from '../../config/constants/routes';
import {
  type ResultGetWorkAttractiveConditions,
  getWorkAttractiveConditions,
} from '../../core/repositories/companyRepository';
import type {IndustryCategoriesReturnValueType} from '../../core/repositories/reportsRepository';
import {getIndustryCategories} from '../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {CompanyListPage} from '../../features/company/components/CompanyListPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  metaInfo: {
    title: string;
    description: string;
    url: string;
  };
  industryCategories?: IndustryCategoriesReturnValueType;
  attractiveConditionsData?: ResultGetWorkAttractiveConditions;
  attractiveConditionQuery?: string[];
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (
    context,
  ): Promise<
    GetServerSidePropsResult<Props & {attractiveConditionQuery?: string[]}>
  > => {
    const props: Props = {
      metaInfo: {
        title: '企業を探す',
        description:
          'セミナー、説明会、選考会など、厳選した外資・日系トップ企業の新卒採用に関する最新情報を随時掲載。',
        url: `${GSSKT_HOST}${COMPANIES}`,
      },
    };

    const industryCategoriesData = await getIndustryCategories({});
    if (!industryCategoriesData.ok) {
      return {
        props,
      };
    }

    const attractiveConditionsData = await getWorkAttractiveConditions({});
    if (!attractiveConditionsData?.ok) {
      return {
        props: {
          ...props,
          industryCategories: industryCategoriesData,
        },
      };
    }
    const {cookies} = context.req;
    const {query} = context;
    const {isValid} = validateCookie(cookies);

    if (query?.wac) {
      if (Array.isArray(query.wac)) {
        props.attractiveConditionQuery = query.wac as string[];
      } else {
        props.attractiveConditionQuery = [query.wac as string];
      }
    }

    if (!isValid)
      return {
        props: {
          ...props,
          industryCategories: industryCategoriesData,
          attractiveConditionsData,
        },
      };

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          ...props,
          industryCategories: industryCategoriesData,
          attractiveConditionsData,
        },
      };
    }

    return {
      props: {
        ...props,
        industryCategories: industryCategoriesData,
        attractiveConditionsData,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  metaInfo,
  industryCategories,
  attractiveConditionsData,
  attractiveConditionQuery = [],
}: Props) => {
  const title = metaInfo.title + TITLE_SUFFIX;
  return (
    <>
      <Head>
        <title key="title">{title}</title>
        <meta key="og:title" property="og:title" content={metaInfo.title} />
        <meta key="og:url" property="og:url" content={metaInfo.url} />
        <meta name="description" content={metaInfo?.description} />
        <meta
          name="keywords"
          content="外資・日系トップ企業,企業一覧,外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES・体験記,注目企業"
        />
        <link rel="canonical" href={metaInfo.url} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>
      <CompanyListPage
        title={metaInfo.title}
        industryCategories={industryCategories}
        attractiveConditionsData={attractiveConditionsData}
        attractiveConditionQuery={attractiveConditionQuery}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
